import React, { useState, useEffect } from "react";
import { permissionService } from "../services/permissionService";

const PermissionsManagement = () => {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingPermission, setEditingPermission] = useState(null);
  const [formData, setFormData] = useState({
    permission_key: "",
    permission_name: "",
  });

  useEffect(() => {
    loadPermissions();
  }, []);

  const loadPermissions = async () => {
    setLoading(true);
    try {
      const response = await permissionService.getPermissions();
      setPermissions(response.data.data);
    } catch (error) {
      console.error("Error loading permissions:", error);
      alert(
        "Error loading permissions: " +
          (error.response?.data?.message || error.message)
      );
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.permission_key.trim() || !formData.permission_name.trim()) {
      alert("Permission key and name are required!");
      return;
    }
    try {
      setSaving(true);
      if (editingPermission) {
        await permissionService.updatePermission(editingPermission.id, {
          permission_key: formData.permission_key,
          permission_name: formData.permission_name,
        });
        alert("Permission updated successfully!");
      } else {
        await permissionService.createPermission({
          permission_key: formData.permission_key,
          permission_name: formData.permission_name,
        });
        alert("Permission created successfully!");
      }
      setFormData({ permission_key: "", permission_name: "" });
      setShowForm(false);
      setEditingPermission(null);
      loadPermissions();
    } catch (error) {
      console.error("Error saving permission:", error);
      alert(error.response?.data?.message || "Error saving permission");
    }
    setSaving(false);
  };

  const handleEdit = (permission) => {
    setEditingPermission(permission);
    setFormData({
      permission_key: permission.permission_key,
      permission_name: permission.permission_name,
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this permission?")) {
      return;
    }
    try {
      setLoading(true);
      // Note: You may need to add deletePermission endpoint in backend
      alert("Delete functionality needs to be implemented in backend");
      loadPermissions();
    } catch (error) {
      alert("Error: " + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 ">
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Permissions Management</h2>
          <button
            onClick={() => {
              setShowForm(true);
              setEditingPermission(null);
              setFormData({ permission_key: "", permission_name: "" });
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            + Create Permission
          </button>
        </div>

        {showForm && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6 rounded-lg shadow-lg">
              <h3 className="text-xl font-bold mb-4">
                {editingPermission
                  ? "Edit Permission"
                  : "Create New Permission"}
              </h3>
              <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Permission Key *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.permission_key}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        permission_key: e.target.value,
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
                    placeholder="e.g., manage_users, view_reports"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Permission Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.permission_name}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        permission_name: e.target.value,
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
                    placeholder="e.g., Manage Users, View Reports"
                  />
                </div>
                <div className="col-span-2 flex gap-2">
                  <button
                    type="submit"
                    disabled={saving}
                    className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {saving
                      ? "Saving..."
                      : editingPermission
                      ? "Update"
                      : "Create"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setEditingPermission(null);
                      setFormData({ permission_key: "", permission_name: "" });
                    }}
                    className="bg-gray-500 text-white px-6 py-2 rounded-md hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-8">Loading...</div>
        ) : (
          <div className="bg-white rounded-lg shadow-md overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-teal-600 to-emerald-600">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">
                    Sr. No.
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">
                    ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">
                    Permission Key
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">
                    Permission Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">
                    Created At
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {permissions.map((permission, index) => (
                  <tr key={permission.id}>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {index + 1}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {permission.id}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {permission.permission_key}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {permission.permission_name}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {permission.created_at
                        ? new Date(permission.created_at).toLocaleDateString(
                            "en-GB"
                          )
                        : "-"}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleEdit(permission)}
                        className="text-blue-600 hover:text-blue-900 p-2 rounded hover:bg-blue-50 transition-colors"
                        title="Edit Permission"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PermissionsManagement;
